import React, { useCallback, useState } from 'react';
import { VotingCard } from '../VotingCard/VotingCard';
import { Icons, IconsVariants, UserName } from '../../UI';
import { useNavigate, useLocation } from 'react-router-dom';
import { Links, LinksVariants } from '../../UI/Links/Links';
import { getToken } from '../../utils/auth';
import { setPendingVote } from '../../utils/pendingVote';

import './QuestionCard.css';
import { countComments } from '../../utils/helpers';
import { ShareButton } from './ShareButton';
import { useAnswerQuestionMutation, useBookMarkQuestionMutation } from '../../apis';
import { Avatar } from '../Avatar/Avatar';
import type { Question, QuestionDetail } from '../../types';

interface QuestionCardProps {
  data: Question | QuestionDetail;
}

export const QuestionCard: React.FC<QuestionCardProps> = ({ data }) => {

  const [bookMarkQuestion] = useBookMarkQuestionMutation();
  const [answerQuestion] = useAnswerQuestionMutation();


  const userIsloggedIn = getToken();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUserId = localStorage.getItem('userId');

  // Logged-out users get the login modal (which links to register) instead of
  // a silent no-op when they try to act.
  const requireAuth = useCallback(
    () => navigate('/login', { state: { backgroundLocation: location } }),
    [navigate, location],
  );

  // Vote-first: stash the tapped answer before routing to auth, so it can be
  // replayed and "locked in" once they sign in (see usePendingVoteReplay).
  const requireVote = useCallback(
    (answer: boolean) => {
      setPendingVote({ questionId: data.questionId, answer });
      navigate('/login', { state: { backgroundLocation: location } });
    },
    [data.questionId, navigate, location],
  );

  const displayName = data.creator.displayName;
  const userName = data.creator.userName;
  const userId = data.creator.creatorId;
  const profileImgUrl = data.creator.profileImgUrl;
  const question = data.title;
  const questionId = data.questionId;
  const likes = data.likes;
  const dislikes = data.dislikes;
  const answered = !currentUserId ? null : data.answered;
  const commentsCount = Array.isArray(data.comments) ? countComments(data.comments) : data.comments;
  const [isBookmarked, setIsBookmarked] = useState<boolean>(data.bookmarked);


  const onCardClick = (_e: React.MouseEvent<HTMLDivElement>) => {
    navigate(`/question/${questionId}`);
  };

  const onBookMark = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (!getToken()) {
      requireAuth();
      return;
    }

    setIsBookmarked(prev => !prev);
    const userId = localStorage.getItem('userId');
    bookMarkQuestion({ questionId, userId: userId ?? '' });
  }, [questionId, bookMarkQuestion, requireAuth]);

  const onAnswerHandler = (answer: boolean) => {
    if (!getToken()) {
      requireAuth();
      return;
    }
    answerQuestion({ questionId, answer });
  };


  const handleNavigation = (event: React.MouseEvent) => {
    if ((event.target as HTMLElement).tagName === 'A') event.stopPropagation();
  };

  return (
    <div onClick={onCardClick} className='flex bg-app-card rounded-app-lg first:mt-0 mt-app-base p-app-lg cursor-pointer'>
      <Avatar
        userId={userId}
        displayName={displayName}
        userName={userName}
        imageUrl={profileImgUrl}
      />
      <div className='flex flex-1 flex-col px-app-base'>
        <div className='flex flex-col pb-app-base'>
          <div onClick={handleNavigation}>
            <Links
              variant={LinksVariants.PRIMARY} to={`/profile/${userId}`}>
              {displayName}
            </Links>
            <div className='flex-0' >
              <UserName userName={userName} />
            </div>
          </div>
          <div className='flex mt-app-base'>
            <span>
              {question}
            </span>
          </div>
        </div>
        <div className='flex flex-row'>
          <div className='flex flex-col flex-1 mr-app-base'>
            <VotingCard likes={likes} dislikes={dislikes} answered={answered}
              onAnswer={onAnswerHandler}
              canVote={!!userIsloggedIn}
              onRequireAuth={requireVote}
            />

            <div className='flex justify-between text-app-gray'>
              <div>
                <button className="question-card-actions-buttons">
                  <Icons variant={IconsVariants.COMMENT} />
                </button>
                {
                  commentsCount != null
                    ? <span className='text-app-base ml-app-xs'>{commentsCount}</span>
                    : null
                }
              </div>

              {
                userIsloggedIn
                  ? <button className="question-card-actions-buttons" onClick={onBookMark}>
                    <Icons
                      variant={isBookmarked ? IconsVariants.BOOKMARK_FULL : IconsVariants.BOOKMARK}
                      styles={isBookmarked ? 'text-app-secondary' : undefined}
                    />
                  </button>
                  : null
              }

              <ShareButton questionId={questionId} title={question} />

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
